import { ERROR_MESSAGE, SIGN_UP_VALIDATE } from '~/constants'

export const formatWithCommas = (value: string | number) => {
  return value?.toString().replace(/(\d)(?=(?:\d{3})+(?!\d))/g, '$1,')
}

export const discountRate = (fullPrice: number, salesPrice: number) => {
  const rate = Math.round(((fullPrice - salesPrice) / fullPrice) * 100)
  return rate <= 0 ? '' : `${rate}%`
}

export const registerWindowInterface = () => {
  window.setUserId = function (userId) {
    const userStore = useCommonStore()
    userStore.setUserId(userId)
  }
}

export const deepClone = <T>(data: T): T => JSON.parse(JSON.stringify(data))

export const createScrollLogObserver = ({
  type,
  callback,
  options
}: {
  type: 'MIDDLE' | 'ALL'
  callback: (element?: IntersectionObserverEntry) => void
  options?: IntersectionObserverInit
}) => {
  let observer: IntersectionObserver

  if (type === 'MIDDLE') {
    // innerHeight의 1/3지점에 element가 위치하는 경우 log전송
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((targetEntry) => {
          const { intersectionRatio } = targetEntry

          if (intersectionRatio > 0 && intersectionRatio < 1) {
            callback(targetEntry)
          }
        })
      },
      { rootMargin: `0px 0px -${window.innerHeight / 3}px 0px`, ...options }
    )
  } else {
    // element 전체가 viewport에 노출되는 시점에 log전송 ( element의 크기가 viewport보다 작은경우는 해당되지 않습니다. )
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((targetEntry) => {
          const { intersectionRatio } = targetEntry
          if (intersectionRatio > 0) {
            callback(targetEntry)
          }
        })
      },
      { threshold: 1, ...options } // 1이면 element 전체가 viewport에 노출되었는지 체크합니다.
    )
  }
  return observer
}

type OsType = 'ANDROID' | 'IOS' | 'ETC'
export const osUtils: {
  isAndroid: boolean
  isIOS: boolean
  osType: OsType
} = (() => {
  const ua = navigator.userAgent.toLowerCase()
  const isAndroid = (() => !!ua.match(/android/))()
  const isIOS = (() => !!ua.match(/iphone|ipad|ipod/))()
  const osType = (() => {
    if (isAndroid) return 'ANDROID'
    if (isIOS) return 'IOS'
    return 'ETC'
  })()
  return {
    isAndroid,
    isIOS,
    osType
  }
})()

export const getStringParams = (params: { [key: string]: string }) =>
  Object.entries(params).reduce((prev, [key, value]) => {
    if (value) {
      if (prev) return `${prev}&${key}=${value}`
      return `${key}=${value}`
    }
    return prev
  }, '')

export const isStg = () => location.host.startsWith('stg') || location.host.startsWith('localhost')
/* [ stock ] setting url prefix by env */
export const getPrefix = () => {
  if (location.host.startsWith('stg') || location.host.startsWith('localhost')) {
    return 'stg-'
  } else if (location.host.startsWith('dev')) {
    return 'dev-'
  }
  return ''
}
export const APOLLO_HOSTNAME = `https://${getPrefix()}webview.sktapollo.com`

export const validName = (text: string) => {
  return text.trim().length >= 2 && SIGN_UP_VALIDATE.NAME.test(text)
}

export const validPhoneNumber = (text: string) => {
  return /^01([0|1|6|7|8|9])?([0-9]{3,4})?([0-9]{4})$/.test(text) && text.length > 9
}

export const getLogNumber = (index: number) => `${index < 10 ? `0${index}` : index}`

export const numberOnly = (text: string) => {
  const reg = /[^0-9]/g
  const num = text.replace(reg, '')
  return num
}

export const getFormattedData = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')

  return `${year}.${month}.${day}`
}

export const getErrorMessage = (code?: string, message?: string) => {
  const errorMessage: string = ERROR_MESSAGE[code as keyof typeof ERROR_MESSAGE] || message || ''

  return errorMessage
}

export const formatPhoneNumber = (number: string) => {
  return number.replace(/(\d{3})(\d{4})(\d{4})/, '$1 $2 $3')
}

export const getWindowResult = (data: string) => {
  try {
    return JSON.parse(data)
  } catch (e) {
    return data
  }
}

// '2409194'
// 주민번호앞 7자리

export const isUnder18 = (residentNumber: string): boolean => {
  // 주민등록번호 앞 6자리 (YYMMDD)
  const birthDatePart = residentNumber.substring(0, 6)

  // 생년월일 파싱
  const year = parseInt(birthDatePart.substring(0, 2), 10)
  const month = parseInt(birthDatePart.substring(2, 4), 10) - 1 // 월은 0부터 시작
  const day = parseInt(birthDatePart.substring(4, 6), 10)

  /**
   * 주민등록번호 뒷자리의 첫 번째 숫자
   *
   * 한국인
   * 1900년대 남자 1
   * 1900년대 여자 2
   * 2000년대 남자 3
   * 2000년대 남자 4

   * 외국인
   * 1900년대 남자 5
   * 1900년대 여자 6
   * 2000년대 남자 7
   * 2000년대 남자 8
   */

  const identifierCode = parseInt(residentNumber.charAt(6), 10)

  // 출생 연도 결정
  let fullYear = 0
  if ([1, 2, 5, 6].includes(identifierCode)) {
    fullYear = 1900 + year
  } else if ([3, 4, 7, 8].includes(identifierCode)) {
    fullYear = 2000 + year
  }

  // 생년월일 객체 생성
  const birthDate = new Date(fullYear, month, day)

  // 현재 날짜 객체 생성
  const today = new Date()

  // 18년 전 날짜 계산
  const cutoffDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate())

  // 만 18세 미만인지 여부 판단
  return birthDate > cutoffDate
}

export const extractDomain = (url: string | undefined): string => {
  if (!url) return ''
  const hostname = new URL(url).hostname
  return hostname.replace(/^www\./, '')
}
